/**
 * Cómo se le nombran los atajos a quien usa el panel, según su sistema.
 *
 * El comportamiento es el mismo en Mac y en Windows —es el mismo evento—, pero decirle "Ctrl+V" a
 * alguien en Mac lo manda a probar una tecla que ahí no hace nada.
 *
 * Se detecta por plataforma y no por user-agent completo porque la única distinción que hace falta
 * es Mac contra el resto.
 */
export function isMacPlatform(): boolean {
  return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '')
}

/** `⌘V` en Mac, `Ctrl+V` en el resto. */
export function pasteKeyLabel(): string {
  return isMacPlatform() ? '⌘V' : 'Ctrl+V'
}

/**
 * El atajo para reportar un problema: `⌥ R` en Mac, `Alt + R` en el resto.
 *
 * Es Alt+R y no Ctrl/⌘+Shift+B porque esa combinación ya la usa Chrome para mostrar y ocultar la
 * barra de marcadores: el navegador se la queda antes de que la aplicación la vea.
 */
export function reportShortcutLabel(): string {
  return isMacPlatform() ? '⌥ R' : 'Alt + R'
}

/**
 * Si el evento de teclado es el atajo de reportar.
 *
 * !! Se compara `event.code` y NO `event.key`. En Mac, con Option presionada, `event.key` vale `'®'`
 * y en distribuciones que no son la de Estados Unidos puede valer cualquier otra cosa. `code` es la
 * tecla física y es igual en todas.
 *
 * Se ignora mientras se está escribiendo: robarle una tecla a quien llena un formulario sería peor
 * que no tener atajo.
 */
export function isReportShortcut(event: KeyboardEvent): boolean {
  if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false
  if (event.code !== 'KeyR') return false
  return !isTypingTarget(event.target)
}

/** Si el foco está en algo donde se escribe. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el?.tagName) return false
  const tag = el.tagName.toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true
}
